// Review-surface consolidation §2 — staff-editable package due date.
// Extracted from the (now thin) standalone staging-review page so the
// queue slide-over keeps the inline edit → PATCH /due-date capability.
// Persists via collectionPackagesApi.updateDueDate (date-only) and
// invalidates the detail + queue caches so the row re-renders with the new
// date + severity. The backend enforces the today-or-future rule and the
// Loan-Administrator permission.

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { Button, Input } from "@/design-system/components/ui";
import { collectionPackagesApi } from "@/api/collectionPackages";
import { formatCalendarDate } from "@/utils/datetime";
import { operationalToday } from "@/design-system/lib/format";

export function PackageDueDateEditor({
  packageId,
  dueDate,
}: {
  packageId: string;
  dueDate: string | null;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(dueDate ? dueDate.slice(0, 10) : "");
  const [error, setError] = useState<string | null>(null);
  // The floor must agree with the server rule it mirrors, and that rule is
  // `CollectionPackageService.UpdateDueDateAsync`'s `due < PlatformDisplayZone
  // .DayOf(UtcNow)` — the firm's day, not the reader's and not UTC. Read off
  // the UTC day it offered only tomorrow from 17:00 Phoenix, so staff setting a
  // package due TODAY could not pick today.
  const today = operationalToday();

  const mutation = useMutation({
    mutationFn: (next: string | null) =>
      collectionPackagesApi.updateDueDate(packageId, next),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["staging-detail"] });
      qc.invalidateQueries({ queryKey: ["collection-staging"] });
      qc.invalidateQueries({ queryKey: ["collection-package"] });
      setEditing(false);
      setError(null);
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!editing) {
    return (
      <div className="flex items-center gap-space-2 text-small">
        <span className="text-caption uppercase tracking-label text-gold-shadow">
          Due date
        </span>
        <span className="text-onyx">{dueDate ? formatCalendarDate(dueDate) : "Not set"}</span>
        <button
          type="button"
          onClick={() => {
            setValue(dueDate ? dueDate.slice(0, 10) : "");
            setEditing(true);
          }}
          className="text-caption text-onyx hover:text-gold-shadow"
        >
          <Pencil size={12} className="mr-1 inline" /> Edit
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-1">
      <div className="flex items-center gap-space-2">
        <span className="text-caption uppercase tracking-label text-gold-shadow">
          Due date
        </span>
        <Input
          type="date"
          value={value}
          min={today}
          onChange={(e) => setValue(e.target.value)}
          className="w-44"
          aria-label="Due date"
        />
        <Button
          variant="secondary"
          onClick={() => mutation.mutate(value || null)}
          loading={mutation.isPending}
        >
          Save
        </Button>
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            setError(null);
          }}
          className="text-caption text-onyx hover:text-gold-shadow"
        >
          Cancel
        </button>
      </div>
      {error && (
        <p className="text-caption text-status-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
